import { SCHEDULING_USER_PREFIXES, CAPACITY_EXEMPT_USER_PREFIXES } from './constants';

/**
 * True when the logged-in Auth0 profile belongs to the scheduling team.
 *
 * Auth0 exposes the login three different ways depending on the connection --
 * `email` and `name` are full addresses, `nickname` is already the bare prefix.
 * All three are checked, lowercased and split on '@', so the same person matches
 * regardless of which field their connection populates.
 *
 * Also accepts a plain string, so callers holding only a username (e.g. the
 * capitalized `usernamee` the dialogs are handed) can use the same test.
 */
export function isSchedulingUser(profile: any): boolean {
  return matchesLoginPrefix(profile, SCHEDULING_USER_PREFIXES);
}

// True when the login is exempt from the over-capacity modal and snackbars.
// Accounting is unaffected -- their jobs still consume hours and flag the date.
export function isCapacityExemptUser(profile: any): boolean {
  return matchesLoginPrefix(profile, CAPACITY_EXEMPT_USER_PREFIXES);
}

// Shared by both prefix checks above so their matching rules cannot drift apart.
function matchesLoginPrefix(profile: any, prefixes: string[]): boolean {
  if (!profile) { return false; }

  const candidates = typeof profile === 'string'
    ? [profile]
    : [profile.email, profile.name, profile.nickname];

  return candidates
    .filter(v => typeof v === 'string' && v.length > 0)
    .map(v => v.toLowerCase().trim().split('@')[0])
    .some(prefix => prefixes.includes(prefix));
}

// 'YYYY-MM-DD' or ISO timestamp -> 'MM/DD/YYYY' without time-zone conversion (for DATE columns).
export function formatDateOnly(value: any): string {
  if (!value) { return ''; }
  const s = typeof value === 'string' ? value : new Date(value).toISOString();
  const [y, m, d] = s.substring(0, 10).split('-');
  return (y && m && d) ? `${m}/${d}/${y}` : '';
}

export function convert_schedule_date_to_string_date(date: any): string {

    if(!date || !date._i){
      return '';
    }

    let year = date._i.year;
    let month = date._i.month;
    month = month + 1;

    if (month < 10) {
      month = '0' + month;
    }

    let day = date._i.date;
    if (day < 10) {
      day = '0' + day;
    }
    return year + '-' + month + '-' + day;
}

export function isInDST(date) {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  // Basically, we'll compare timezone offsets for the current timezone. 
  // Pick a month where it's definitely DST, and one where it's definitely ST
  // Offset will be smaller during DST. 

  const currentOffset = date.getTimezoneOffset();
  const januaryOffset = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
  const julyOffset = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();

  return currentOffset < Math.max(januaryOffset, julyOffset);
}

/**
 * Build the URL of a location's Extra Notes feed (`/m/extra-notes/:location_id`).
 *
 * The feed page is a server-rendered EJS page outside the Angular app, so it has
 * no Auth0 session of its own -- it learns who the poster is ONLY from these two
 * query params, which it stamps onto `locposts.employee_name` / `employee_id`.
 *
 * Kept in one place because there are two callers (the Locations grid's
 * `openExtraNotes`, and the `/extra-notes/:location_id` deep-link bounce route
 * used by external CSV reports). If the two ever derived `usernamee` differently,
 * the same person would be attributed under two different names on their posts.
 *
 * NOTE: `usernamee` is the CAPITALIZED Auth0 nickname (see
 * `location.component.ts`: `nickname.charAt(0).toUpperCase() + nickname.slice(1)`).
 * Pass it already capitalized -- this helper does not transform it, so that the
 * value stored on a post matches every other `usernamee` the app writes.
 */
export function buildExtraNotesUrl(
  baseUrl: string,
  locationId: any,
  usernamee: string,
  employeeId: any
): string {
  return `${baseUrl}/m/extra-notes/${locationId}`
    + `?user=${encodeURIComponent(usernamee || '')}`
    + `&uid=${encodeURIComponent(employeeId != null ? String(employeeId) : '')}`;
}
